'use client';

import { type ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import { type TextAreaState } from '@videoblocks/design-system';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { type VideoStockItemResult } from '../../_shared/_actions/searchLibraryVideos';
import { type Suggestion } from '../../_shared/_components/forms/TextAreaInputField/TextAreaInputField';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    validateVideoEditForm,
    VIDEO_EDIT_FORM_DEFAULTS,
    type VideoEditFormErrors,
    type VideoEditFormState,
} from '../../_shared/_context/VideoEditFormState';
import {
    AI_TOOLKIT_TELEMETRY_ACTIONS,
    emitAiToolkitTag,
    toSuggestionAction,
    toUseCaseAction,
} from '../../_shared/_context/aiToolkitTelemetry';
import { MAX_PROMPT_LENGTH, VIDEO_EDIT_CREDIT_COST } from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES, type AiTaskSubmitRouteResponse } from '../../_shared/_context/types';
import { VIDEO_EDIT_USE_CASE_PLACEHOLDERS, VIDEO_EDIT_USE_CASE_SUGGESTIONS } from './constants';

const VIDEO_EDIT_ENDPOINT = '/api/video-edit';

export type UseVideoEditFormReturn = {
    formState: VideoEditFormState;
    errors: VideoEditFormErrors;
    promptState: TextAreaState;
    promptHelperText: string | undefined;
    promptPlaceholder: string;
    suggestions: Suggestion[];
    isValid: boolean;
    isSubmitDisabled: boolean;
    submitError: string | null;
    calculatedCost: number;
    onPromptChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptBlur: () => void;
    onUseCaseSelect: (id: string) => void;
    onSuggestionClick: (suggestion: Suggestion) => void;
    onVideoSelect: (item: VideoStockItemResult) => void;
    onVideoDeselect: () => void;
    onSubmit: () => void;
    onConfirm: () => Promise<void>;
};

export const useVideoEditForm = (): UseVideoEditFormReturn => {
    const {
        formStates,
        updateFormState,
        dispatchVideoEditSubmit,
        dispatchConfirmationModal,
        submitStates,
        refreshCredits,
        videoSearch,
        videoEditSubmitError: submitError,
        setVideoEditSubmitError: setSubmitError,
    } = useAiToolkitContext();
    const { setShowSearch } = videoSearch;
    const isInProgress = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT].status === 'inProgress';

    const formState = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT] ?? VIDEO_EDIT_FORM_DEFAULTS;
    const validationActive = formState.validationActive;

    useEffect(() => {
        if (!formStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]) {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, VIDEO_EDIT_FORM_DEFAULTS);
        }
    }, [formStates, updateFormState]);

    const { isValid, errors: schemaErrors } = useMemo(() => validateVideoEditForm(formState), [formState]);

    const isSubmitDisabled = !isValid;

    const errors: VideoEditFormErrors = useMemo(() => {
        if (!validationActive) {
            return {};
        }
        return { ...schemaErrors };
    }, [validationActive, schemaErrors]);

    const promptState: TextAreaState = useMemo(() => {
        if (validationActive && schemaErrors.prompt) {
            return 'error';
        }
        return 'default';
    }, [validationActive, schemaErrors.prompt]);

    const promptHelperText = useMemo((): string | undefined => {
        if (validationActive && schemaErrors.prompt) {
            return schemaErrors.prompt;
        }
        if (formState.prompt.length >= MAX_PROMPT_LENGTH) {
            return `You've reached the maximum allowed length (${MAX_PROMPT_LENGTH.toLocaleString()} characters).`;
        }
        return undefined;
    }, [validationActive, schemaErrors.prompt, formState.prompt.length]);

    const suggestions = useMemo(
        (): Suggestion[] => VIDEO_EDIT_USE_CASE_SUGGESTIONS[formState.useCase] ?? [],
        [formState.useCase],
    );

    const promptPlaceholder = useMemo(
        (): string => VIDEO_EDIT_USE_CASE_PLACEHOLDERS[formState.useCase] ?? '',
        [formState.useCase],
    );

    const updateField = useCallback(
        <K extends keyof VideoEditFormState>(field: K, value: VideoEditFormState[K]): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, [field]: value });
        },
        [formState, updateFormState],
    );

    const onPromptChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>): void => {
            updateField('prompt', e.target.value);
        },
        [updateField],
    );

    const onPromptBlur = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, validationActive: true });
    }, [formState, updateFormState]);

    const onUseCaseSelect = useCallback(
        (id: string): void => {
            updateField('useCase', id);
            emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, toUseCaseAction(id));
        },
        [updateField],
    );

    const onSuggestionClick = useCallback(
        (suggestion: Suggestion): void => {
            updateField('prompt', suggestion.promptText);
            emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, toSuggestionAction(suggestion.label));
        },
        [updateField],
    );

    const onVideoSelect = useCallback(
        (item: VideoStockItemResult): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, {
                ...formState,
                sourceVideo: item.previewUrl,
                contentId: item.contentId,
            });
            dispatchVideoEditSubmit({
                type: 'SELECTION',
                originalVideoUrl: item.previewUrl,
                originalThumbnailUrl: item.thumbnailUrl,
                duration: item.duration,
                contentId: item.contentId,
                aspectRatio: item.aspectRatio,
            });
        },
        [formState, updateFormState, dispatchVideoEditSubmit],
    );

    const onVideoDeselect = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, {
            ...formState,
            sourceVideo: '',
            contentId: null,
        });
        dispatchVideoEditSubmit({ type: 'RESET' });
    }, [formState, updateFormState, dispatchVideoEditSubmit]);

    const onSubmit = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, validationActive: true });

        if (!isValid) {
            return;
        }

        emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, AI_TOOLKIT_TELEMETRY_ACTIONS.generateVideo);
        dispatchConfirmationModal({
            type: 'OPEN_MODAL',
            modal: { type: 'creditConfirmation', category: AI_TOOLKIT_CATEGORIES.VIDEO_EDIT },
        });
    }, [formState, updateFormState, isValid, dispatchConfirmationModal]);

    const onConfirm = useCallback(async (): Promise<void> => {
        if (isInProgress || !formState.contentId) return;
        setShowSearch(false);
        dispatchVideoEditSubmit({
            type: 'GENERATION_IN_PROGRESS',
            startTime: formState.startTime,
            duration: formState.duration,
            contentId: formState.contentId,
        });
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
        setSubmitError(null);

        try {
            const response = await fetch(VIDEO_EDIT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contentId: formState.contentId,
                    prompt: formState.prompt.trim(),
                    startTime: formState.startTime,
                    duration: formState.duration,
                    algoParams: { modelId: formState.model },
                }),
            });
            void refreshCredits();

            const payload = (await response.json()) as AiTaskSubmitRouteResponse;

            if (!response.ok || payload.success !== true) {
                const message =
                    ('message' in payload && payload.message) ||
                    ('error' in payload && payload.error) ||
                    'Video edit failed. Please try again.';
                const failureReason = 'failureReason' in payload ? payload.failureReason ?? null : null;
                setSubmitError(message);
                dispatchVideoEditSubmit({ type: 'GENERATION_ERROR', failureReason });
                return;
            }

            dispatchVideoEditSubmit({
                type: 'GENERATION_IN_PROGRESS',
                pendingTaskId: payload.taskId,
                aiContentId: payload.aiContentId,
                lastAiContentId: payload.lastAiContentId,
                aiContentType: AiContentType.AI_VIDEO_EDIT,
                aiModelName: formState.model,
            });
        } catch (err) {
            void refreshCredits();
            setSubmitError(err instanceof Error ? err.message : 'Video edit failed. Please try again.');
            dispatchVideoEditSubmit({ type: 'GENERATION_ERROR' });
        }
    }, [
        dispatchConfirmationModal,
        dispatchVideoEditSubmit,
        formState,
        isInProgress,
        refreshCredits,
        setShowSearch,
        setSubmitError,
    ]);

    return {
        formState,
        errors,
        promptState,
        promptHelperText,
        promptPlaceholder,
        suggestions,
        isValid,
        isSubmitDisabled,
        submitError,
        calculatedCost: VIDEO_EDIT_CREDIT_COST,
        onPromptChange,
        onPromptBlur,
        onUseCaseSelect,
        onSuggestionClick,
        onVideoSelect,
        onVideoDeselect,
        onSubmit,
        onConfirm,
    };
};
